import { useMemo } from "react";
import { Section, Eyebrow, DisclaimerNote } from "@/components/Primitives";
import LeadForm from "@/components/LeadForm";
import { registerZoom } from "@/lib/api";

export default function Zoom() {
  // Show the local-time equivalent of Wednesday 7:00 PM Mountain Time.
  const localEquivalent = useMemo(() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      // Build the next Wednesday 19:00 America/Denver as a reference.
      const now = new Date();
      const ref = new Date(now);
      const day = ref.getDay();
      const daysUntilWed = (3 - day + 7) % 7 || 7;
      ref.setDate(ref.getDate() + daysUntilWed);
      // Interpret 19:00 Denver — approximate using Denver offset via formatting.
      const denverStr = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/Denver",
        hour: "numeric",
        minute: "2-digit",
        weekday: "long",
        timeZoneName: "short",
      }).format(ref);
      return { tz, denverStr };
    } catch (e) {
      return { tz: "your local time", denverStr: "" };
    }
  }, []);

  return (
    <div className="pb-8">
      <Section className="pt-14 sm:pt-20">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <Eyebrow>Weekly online gathering</Eyebrow>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-stone-900 leading-[1.08] mt-4">
              Wednesdays, 7:00 PM Mountain Time
            </h1>
            <p className="text-lg text-stone-600 leading-relaxed mt-5">
              A warm, weekly online session — a low-pressure way to listen in, ask questions, and
              feel whether this community fits. You're welcome to simply observe.
            </p>

            <div className="mt-7 bg-[#EAF2EC] rounded-2xl p-6 border border-[#4A7C59]/20">
              <p className="text-sm font-semibold text-stone-800 flex items-center gap-2">
                <i className="fa-solid fa-earth-americas text-[#4A7C59]" /> Timezone helper
              </p>
              <p className="text-sm text-stone-700 mt-2 leading-relaxed">
                Sessions are held at <strong>7:00 PM America/Denver (Mountain Time)</strong> every
                Wednesday. Your device timezone is <strong>{localEquivalent.tz}</strong> — please
                convert to your local time when planning to join. A Zoom link is emailed before each
                session.
              </p>
            </div>

            <DisclaimerNote
              className="mt-6"
              text="Group sessions are supportive and educational. They are not group therapy or a substitute for individual clinical care."
            />
          </div>

          <div className="bg-white rounded-[2rem] border border-stone-200 p-8 shadow-sm">
            <h2 className="font-playfair text-2xl text-stone-900 mb-6">Register for the session</h2>
            <LeadForm
              testIdPrefix="zoom"
              submitLabel="Register for Wednesday's session"
              onSubmit={registerZoom}
              consentLabel="I'd like to receive the Zoom link and reminders for the weekly Wednesday session by email."
              fields={[
                { name: "name", label: "Full name", required: true, placeholder: "Your name" },
                { name: "email", label: "Email", type: "email", required: true, placeholder: "you@example.com" },
              ]}
            />
          </div>
        </div>
      </Section>
    </div>
  );
}
